import React, { FC, useEffect, useRef, useState } from 'react';

import {
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';

import { Box } from '@mui/system';

import {
  Cancel,
  CloudDownloadOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';

import {
  adminActions,
  adminDownloadLeadsTemplate,
  adminUploadLeadsFile,
  getLeadsTemplateURL,
  getUploadLeadsResponse,
  HelpTypes,
  isLoadingSelector,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { resetDialog, setDialog } from '../../../../reducers/dialogReducer';
import { OutcomeModalTypes } from '../../../../helpers/Constants';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomButton from '../../../common/CustomButton';
import OutcomeModal from '../../../Modals/OutcomeModal';
import strings from '../../../../localization';
import {
  showError,
  showSuccess,
} from '../../Profile/Pharmacist/PharmacistViewProfile/helper';

import styles from './styles';
import UploadLeadsError from './UploadLeadsError';

interface IUploadLeadsModal {
  userType: HelpTypes;
  onSuccess: () => void;
}

const UploadLeadsModal: FC<IUploadLeadsModal> = ({ onSuccess, userType }) => {
  const dispatch = useAppDispatch();
  const templateURL = useAppSelector(getLeadsTemplateURL);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const uploadLeadsResponse = useAppSelector(getUploadLeadsResponse);
  const uploadSuccess = useAppSelector((state) =>
    successSelector([adminActions.uploadLeadsFile], state));

  const isUploading = useAppSelector((state) =>
    isLoadingSelector([adminActions.uploadLeadsFile], state));

  const isDownloading = useAppSelector((state) =>
    isLoadingSelector([adminActions.downloadLeadsTemplate], state));

  const browseFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleRemove = () => {
    setFile(null);
  };

  const handleUpload = async () => {
    if (!file) {
      showError(dispatch, strings.uploadFile);
      return;
    }
    await dispatch(adminUploadLeadsFile({ file, userType }));
  };

  const handleClose = () => {
    dispatch(resetDialog());
  };

  useEffect(() => {
    dispatch(adminDownloadLeadsTemplate(userType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUploadSuccess = () => {
    if (uploadLeadsResponse.success) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(resetStatus([adminActions.uploadLeadsFile]));
      onSuccess();
      setFile(null);
    } else {
      dispatch(resetStatus([adminActions.uploadLeadsFile]));
      setFile(null);

      dispatch(
        setDialog({
          Component: (
            <OutcomeModal
              type={OutcomeModalTypes.uploadLeadsError}
              message={<UploadLeadsError />}
            />
          ),
          showCloseButton: true,
          heading: {
            title: '',
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (uploadSuccess) {
      onUploadSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSuccess]);

  return (
    <>
      <DialogContent sx={styles.bottomBorder}>
        <Box sx={styles.downloadRow}>
          <CloudDownloadOutlined fontSize="small" />
          <Typography sx={styles.url} component="a" href={templateURL} download>
            {strings.downloadTemplate}
          </Typography>
        </Box>

        <Typography sx={styles.description}>
          {strings.leadsUploadDescription}
        </Typography>

        <Box sx={styles.uploadButton}>
          {file && (
            <Box sx={styles.crossContainer}>
              <IconButton
                size="small"
                onClick={handleRemove}
                sx={styles.removeIcon}
                aria-label={strings.remove}
              >
                <Cancel sx={{ fontSize: 12, color: 'common.gray' }} />
              </IconButton>
              <Typography sx={styles.fileName}>{file.name}</Typography>
            </Box>
          )}
          <CustomButton
            fullWidth={false}
            variant="outlined"
            startIcon={<UploadFileOutlined />}
            label={strings.uploadFile}
            onClick={browseFiles}
            disabled={isUploading || isDownloading}
            type="button"
          />
        </Box>

        <Box
          component="input"
          type="file"
          accept=".xls,.xlsx"
          sx={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <CustomButton
          fullWidth={false}
          variant="text"
          label={strings.cancel}
          onClick={handleClose}
          disabled={isUploading}
          type="button"
        />
        <CustomButton
          fullWidth={false}
          label={strings.upload}
          onClick={handleUpload}
          disabled={!file || isUploading}
          type="button"
        />
      </DialogActions>
    </>
  );
};

export default UploadLeadsModal;
